import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Close } from '@mui/icons-material';
import { KitCard } from './ui/kitCard';
import { useHistory } from 'react-router-dom';
import { TextBodySmallRegular } from './ui/typographies';
import { LabsInput } from './ui/input';
import { useIntl } from 'react-intl';
import { createNewCreabotsProject } from '@modules/blockly/features/hooks';
import {
  openLabsToast,
  selectCreabots,
} from '@sections/creabots/features/creabotsSlice';
import kitOptions from '../constants';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const SelectKitToCreateProjectModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isSelected, setIsSelected] = useState(1);
  const [isIASelected, setIsIASelected] = useState(0);
  const userKitList = useSelector(selectCreabots).kits.list;
  const selectedKit = kitOptions.find(k => k.id === isSelected)
  const filteredKits = (isSelected && selectedKit?.iaModels) ? selectedKit.iaModels.filter((model) => !model.isDisabled) : kitOptions.map((kit) => {
    const alreadyAdded = userKitList.some((k: any) => k.id === kit.id);
    if (!alreadyAdded) {
      return {
        ...kit,
        isDisabled: true,
      };
    }
    return kit;
  });
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    if (
      !isLoading &&
      name &&
      name.toLowerCase() !==
      intl.formatMessage({ id: 'creabots.untitled' }).toLowerCase()
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [name, isLoading]);

  const handleSelected = (kitId: number) => {
    const selectedKit = kitOptions.find(k => k.id === kitId);
    if (selectedKit) {
      setIsSelected(kitId);
      if (selectedKit?.iaModels) {
        setIsIASelected(selectedKit.iaModels[0].id);
      }
    } else {
      // maybe selected kit is an IA model
      const parentKit = kitOptions.find(k => k.iaModels?.some(m => m.id === kitId));
      if (parentKit) {
        setIsSelected(parentKit.id);
        setIsIASelected(kitId);
      }
    }
  };

  const handleOnClose = () => {
    if (!isIASelected) {
      setName("");
      onClose();
    }
    setIsSelected(1);
    setIsIASelected(0);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleSubmit = () => {
    const selectedKit = userKitList.find((k: any) => k.id === isSelected);
    const selectedIAKit = kitOptions.find(k => k.iaModels?.some(m => m.id === isIASelected));
    const model = selectedIAKit?.iaModels?.find(m => m.id === isIASelected)?.model || "";
    if (name && selectedKit) {
      setIsLoading(true);
      createNewCreabotsProject(name, selectedKit.boardId, model)
        .then(id => {
          history.push(`/kits/${selectedKit.urlName}/proyecto-${id}`);
        })
        .catch(_ => {
          setIsLoading(false);
          dispatch(
            openLabsToast({
              open: true,
              message: 'creabots.dashboard.selectKitProjectModal.error',
              severity: 'error',
            })
          );
        });
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleOnClose}
      aria-labelledby="Kit Modal"
      aria-describedby="Modal para agregar kits"
    >
      <Box
        sx={theme => ({
          backgroundColor: theme.palette.common.white,
          borderRadius: theme.spacing(2),
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '914px',
          minWidth: '914px',
          height: 'fit-content',
        })}
      >
        <Box
          sx={theme => ({
            backgroundColor: theme.palette.common.white,
            borderRadius: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '914px',
            minWidth: '914px',
            height: 'fit-content',
          })}
        >
          <Box
            sx={theme => ({
              paddingX: theme.spacing(3),
              paddingY: theme.spacing(2.5),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            })}
          >
            <Typography
              sx={theme => ({
                fontWeight: 500,
                color: theme.palette.text.primary,
                letterSpacing: '-0.015rem',
                fontSize: '1.125rem',
                lineHeight: '24px',
                fontFamily: theme.typography.fontFamily,
              })}
            >
              {intl.formatMessage({
                id: isIASelected ? 'creabots.dashboard.topbar.newProjectAI' : 'creabots.dashboard.topbar.newProject',
              })}
            </Typography>
            <IconButton sx={{ padding: 0 }} onClick={handleOnClose}>
              <Close
                sx={theme => ({
                  height: '24px',
                  width: '24px',
                  color: theme.palette.text.secondary,
                })}
              />
            </IconButton>
          </Box>
          <TextBodySmallRegular
            paddingLeft={3}
            sx={theme => ({ color: theme.palette.text.secondary })}
          >
            {intl.formatMessage({
              id: isIASelected ? 'creabots.dashboard.selectKitProjectModal.descriptionAI' : 'creabots.dashboard.selectKitProjectModal.description',
            })}
          </TextBodySmallRegular>
          <Box
            alignItems="center"
            width="100%"
            paddingX="1.5rem"
            paddingTop="1.5rem"
          >
            <LabsInput
              onChange={handleOnChange}
              name="name"
              value={name}
              fullWidth
              placeholder={intl.formatMessage({ id: 'creabots.projectTitle' })}
            />
          </Box>
          <Box
            sx={theme => ({
              gap: theme.spacing(2.3),
              marginLeft: 0,
              width: '100%',
              height: '100%',
              padding: theme.spacing(3),
              display: 'flex',
              alignItems: 'center',
            })}
          >
            {filteredKits.map((kit: any) => (
              <Box
                key={kit.name}
                sx={{
                  flex: 1,
                  paddingLeft: '0px !important',
                  paddingTop: '0px !important',
                }}
              >
                <KitCard
                  kit={kit}
                  isSelected={isIASelected === kit.id || isSelected === kit.id}
                  setIsSelected={handleSelected}
                  maxImageHeight={isIASelected ? "200px" : "150px"}
                />
              </Box>
            ))}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            justifyContent={'end'}
            paddingX="1.5rem"
            paddingBottom="1.5rem"
          >
            <Box
              display="flex"
              alignItems="center"
              gap="1.5rem"
              justifySelf="end"
            >
              <Button size="small" variant="secondary" onClick={handleOnClose}>
                {intl.formatMessage({
                  id: !isIASelected ? 'common.cancel' : 'common.back',
                })}
              </Button>
              <Button
                variant="primary"
                size="small"
                disabled={disabled}
                onClick={handleSubmit}
              >
                {intl.formatMessage({
                  id: 'common.continue',
                })}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
